// Educational entity & trust comparison reference. Neutral, general information only —
// NOT legal or tax advice. Entity and trust decisions must be confirmed with a licensed
// attorney/CPA. Trust content covers concepts only and never promotes concealment,
// guaranteed immunity, or unqualified personalized structuring.

export const ENTITY_DISCLAIMER =
    "This is general educational information, not legal or tax advice. Rules, costs, and taxes vary by state and change over time. Confirm the right structure for your situation with a licensed attorney and CPA before forming anything.";

export const TRUST_DISCLAIMER =
    "Trusts are powerful but complex legal tools. This is education only — not legal, tax, or estate-planning advice, and not a promise of lawsuit immunity or tax avoidance. Trusts must be drafted and funded correctly by a qualified estate-planning attorney. We never help conceal assets or evade legitimate obligations.";

export const ENTITIES = [
    {
        id: "sole-prop", name: "Sole Proprietorship", tagline: "The default when one person starts working — no filing required.",
        liability: "None — you and the business are legally the same; personal assets are exposed.",
        taxation: "Pass-through. Profits reported on your personal return (Schedule C); pay self-employment tax.",
        complexity: "Lowest", owners: "1 owner",
        pros: ["Fast and free to start", "Simple taxes and recordkeeping", "Full control"],
        cons: ["No liability protection", "Harder to raise capital", "Business ends with the owner"],
        bestFor: "Testing a small side hustle before formalizing.",
    },
    {
        id: "gp", name: "General Partnership (GP)", tagline: "Two or more people in business together, by default.",
        liability: "None — each partner is personally liable, including for the other's actions.",
        taxation: "Pass-through to partners (Form 1065 + K-1s); self-employment tax applies.",
        complexity: "Low", owners: "2+ partners",
        pros: ["Easy to form", "Shared workload and capital", "Pass-through taxes"],
        cons: ["Unlimited, shared personal liability", "Partner disputes without an agreement", "Each partner can bind the business"],
        bestFor: "Early collaborations — ideally with a written partnership agreement.",
    },
    {
        id: "lp", name: "Limited Partnership (LP)", tagline: "General partners run it; limited partners invest passively.",
        liability: "General partners: unlimited. Limited partners: limited to their investment (if passive).",
        taxation: "Pass-through (Form 1065 + K-1s).",
        complexity: "Medium", owners: "1+ general, 1+ limited",
        pros: ["Attracts passive investors", "Limited partners shielded", "Flexible profit splits"],
        cons: ["General partner fully liable", "State filing + agreement required", "Limited partners can't manage"],
        bestFor: "Real estate and investment ventures with passive backers.",
    },
    {
        id: "llc", name: "Limited Liability Company (LLC)", tagline: "The popular, flexible choice for most new businesses.",
        liability: "Strong — members' personal assets are generally separate from business debts.",
        taxation: "Default pass-through (single = Schedule C; multi = 1065). Can elect S-corp or C-corp taxation.",
        complexity: "Medium", owners: "1+ members", popular: true,
        pros: ["Liability protection with simple operations", "Flexible taxation and ownership", "Credibility with banks and clients"],
        cons: ["State filing + annual fees", "Self-employment tax unless S-corp elected", "Must keep finances separate to preserve protection"],
        bestFor: "Most small businesses wanting protection without corporate formality.",
    },
    {
        id: "s-corp", name: "S Corporation (S-Corp)", tagline: "A tax election (for an LLC or corp) that can lower self-employment tax.",
        liability: "Strong — same liability shield as the underlying LLC/corporation.",
        taxation: "Pass-through, but owners take a reasonable salary (payroll tax) + distributions (no SE tax). Form 1120-S.",
        complexity: "Medium-High", owners: "Up to 100 U.S. shareholders",
        pros: ["Potential self-employment tax savings", "Pass-through avoids double taxation", "Liability protection"],
        cons: ["Payroll + reasonable-salary rules", "More bookkeeping and compliance", "Ownership restrictions (US persons, one class)"],
        bestFor: "Profitable owner-operators once salary + payroll make sense (confirm with a CPA).",
    },
    {
        id: "c-corp", name: "C Corporation (C-Corp)", tagline: "The structure for raising venture capital and scaling.",
        liability: "Strong — shareholders generally shielded from corporate debts.",
        taxation: "Taxed at the corporate level; dividends taxed again to shareholders (potential double taxation). Form 1120.",
        complexity: "High", owners: "Unlimited shareholders, multiple classes",
        pros: ["Best for outside investors and stock options", "Unlimited growth and share classes", "Some fringe-benefit advantages"],
        cons: ["Double taxation on dividends", "Most formalities (board, minutes, filings)", "Higher setup and admin cost"],
        bestFor: "Startups raising venture capital or planning to go public.",
    },
    {
        id: "nonprofit", name: "Nonprofit Corporation (501(c)(3))", tagline: "Mission-driven organizations seeking tax-exempt status.",
        liability: "Strong — directors/members generally shielded.",
        taxation: "Can apply for federal tax exemption; must serve an exempt purpose and reinvest surplus.",
        complexity: "High", owners: "No owners — governed by a board",
        pros: ["Tax-exempt if approved", "Eligible for grants and donations", "Mission and public trust"],
        cons: ["Strict IRS/state compliance", "No private profit distribution", "Board governance required"],
        bestFor: "Charitable, educational, or community-benefit missions.",
    },
];

export const TRUSTS = [
    {
        id: "revocable", name: "Revocable Living Trust", tagline: "Keep control now; avoid probate later.",
        control: "You keep full control and can change or revoke it anytime.",
        protection: "Little creditor/lawsuit protection while you're alive (you still control the assets).",
        taxation: "Tax-neutral while living — reported on your personal return.",
        bestFor: "Avoiding probate, privacy, and smooth transfer of assets at death.",
        cautions: "Not an asset-protection or tax-avoidance tool on its own; must be funded (retitle assets) to work.",
    },
    {
        id: "irrevocable", name: "Irrevocable Trust", tagline: "Give up control to gain protection and planning benefits.",
        control: "You generally cannot change it; assets leave your estate.",
        protection: "Stronger protection from creditors and estate taxes when set up properly and in advance.",
        taxation: "May be a separate taxpayer; can reduce estate tax exposure.",
        bestFor: "Estate-tax planning, legacy goals, and long-term protection.",
        cautions: "Loss of control is real and often permanent — only with qualified legal/tax guidance.",
    },
    {
        id: "apt", name: "Asset Protection Trust (DAPT)", tagline: "Designed to shield assets from future creditors.",
        control: "Usually irrevocable with an independent trustee; limited settlor control.",
        protection: "Can protect assets from future (not existing/known) creditors in states/jurisdictions that allow them.",
        taxation: "Varies by structure and jurisdiction.",
        bestFor: "Professionals and owners in higher-liability fields planning ahead.",
        cautions: "Timing and honesty are everything — never used to dodge known claims or hide assets; state-specific.",
    },
    {
        id: "land", name: "Land Trust", tagline: "Hold real estate privately.",
        control: "You (or a beneficiary) direct the trustee; often revocable.",
        protection: "Mainly privacy of ownership — limited liability protection by itself.",
        taxation: "Typically tax-neutral; income flows to the beneficiary.",
        bestFor: "Keeping property ownership private; often paired with an LLC for protection.",
        cautions: "Privacy is not protection — combine with proper liability structures.",
    },
    {
        id: "spendthrift", name: "Spendthrift Trust", tagline: "Protect a beneficiary from creditors — and from themselves.",
        control: "Trustee controls distributions; the beneficiary can't assign away their interest.",
        protection: "Shields trust assets from the beneficiary's creditors until distributed.",
        taxation: "Depends on trust type (often a separate taxpayer).",
        bestFor: "Leaving money to heirs who need spending guardrails.",
        cautions: "Rules vary by state; drafting matters.",
    },
    {
        id: "dynasty", name: "Dynasty Trust", tagline: "Pass wealth across generations, tax-efficiently.",
        control: "Irrevocable; long-term trustee management across generations.",
        protection: "Long-term creditor and estate-tax protection for descendants.",
        taxation: "Can minimize transfer taxes over multiple generations.",
        bestFor: "Multi-generational legacy and family wealth planning.",
        cautions: "Complex, long-lived, and state-dependent — specialist attorney required.",
    },
    {
        id: "charitable", name: "Charitable Trust (CRT / CLT)", tagline: "Blend giving with income or tax planning.",
        control: "Irrevocable; split between charitable and non-charitable beneficiaries.",
        protection: "Removes assets from your estate; supports a cause.",
        taxation: "Potential income-tax deduction and estate-tax benefits.",
        bestFor: "Donors wanting income or tax benefits alongside charitable giving.",
        cautions: "Irrevocable commitment; coordinate with tax and legal advisors.",
    },
    {
        id: "business-trust", name: "Business / Statutory Trust", tagline: "Hold a business or assets in trust form.",
        control: "Trustee manages for beneficiaries under a trust agreement.",
        protection: "Can offer liability separation similar to an entity when properly structured.",
        taxation: "Varies — may be taxed like a partnership or corporation.",
        bestFor: "Certain investment funds and specialized holding structures.",
        cautions: "Niche and technical — verify fit with counsel before using.",
    },
];
